import { Log, LogCategory, Operation as LogOperation } from '@augloop/logging';
import { SchemaObject } from '@augloop/types-core';
import { TextTileDelta, TextTileDeltaType, TextTile, FormattedTextTile, TextTileDeltaUnit } from '@augloop/types-text';
const nonContentDeltaTypes = [TextTileDeltaType.CursorUpdate, TextTileDeltaType.FormattingUpdate, TextTileDeltaType.OtherNonContentUpdate, TextTileDeltaType.AttributionUpdate];
export function applyTextTileDelta(delta, tile, _clientMetadataFlights) {
    const applyDeltaOp = new LogOperation({
        operationName: 'ApplyTextTileDeltaForLocalWorkflows',
        dimension0: '0',
        success: true
    }).start();
    try {
        if (!tile) {
            applyDeltaOp.success = false;
            applyDeltaOp.resultDescription = `Unable to apply text tile delta, parent tile is undefined`;
            Log.info(0x201d685d /* tag_6hw73 */, LogCategory.CoreDefault, applyDeltaOp.stop());
            return undefined;
        }
        if (SchemaObject.getTypeNameFor(tile) !== TextTile.getTypeName()) {
            applyDeltaOp.success = false;
            applyDeltaOp.resultDescription = `Unable to apply text tile delta, parent tile is not proper type: expected ${TextTileDelta.getTypeName()}, received ${SchemaObject.getTypeNameFor(tile)}`;
            Log.info(0x201d685e /* tag_6hw74 */, LogCategory.CoreDefault, applyDeltaOp.stop());
            return undefined;
        }
        const textTile = tile;
        const textTileDelta = delta;
        const content = textTile.content;
        let updatedContent = '';
        if (textTileDelta.position === undefined || textTileDelta.position < 0) {
            applyDeltaOp.success = false;
            applyDeltaOp.resultDescription = `Unable to apply text tile delta, invalid text tile position`;
            Log.info(0x201d685f /* tag_6hw75 */, LogCategory.CoreDefault, applyDeltaOp.stop());
            return undefined;
        }
        switch (textTileDelta.deltaType) {
            case TextTileDeltaType.Add:
                if (textTileDelta.position < content.length) {
                    updatedContent = `${content.slice(0, textTileDelta.position)}${textTileDelta.content}${content.slice(textTileDelta.position, content.length)}`;
                }
                else {
                    updatedContent = content + textTileDelta.content;
                }
                break;
            case TextTileDeltaType.Update:
                if (!textTileDelta.content && textTileDelta.unit === TextTileDeltaUnit.Sentence) {
                    applyDeltaOp.dimension0 = '1';
                }
                updatedContent = `${content.slice(0, textTileDelta.position)}${textTileDelta.content}${content.slice(textTileDelta.position + (textTileDelta.length || 0), content.length)}`;
                break;
            case TextTileDeltaType.Delete:
                updatedContent = `${content.slice(0, textTileDelta.position)}${content.slice(textTileDelta.position + (textTileDelta.length || 0), content.length)}`;
        }
        Log.info(0x201e004f /* tag_6h6bp */, LogCategory.CoreDefault, applyDeltaOp.stop());
        return new TextTile({
            content: updatedContent
        });
    }
    catch (error) {
        applyDeltaOp.success = false;
        applyDeltaOp.resultDescription = `Error applying text tile delta: ${error}`;
        Log.info(0x201d6860 /* tag_6hw76 */, LogCategory.CoreDefault, applyDeltaOp.stop());
        return undefined;
    }
}
/**
 * Gets the updated formatted ranges of a formatted text tile based on the delta
 * @param textTile the text tile to apply the delta
 * @param textTileDelta the delta to apply
 * @returns An array of updated formatted ranges based on the formatted text tile after the delta is applied
 */
function getUpdatedFormattedRangeFromFormattedTextTileDelta(textTile, textTileDelta) {
    var _a, _b, _c, _d;
    const range = findRangeForDeltaType(textTile.formattedRanges, textTileDelta.deltaType, textTileDelta.position);
    let updatedRanges = textTile.formattedRanges ? [...textTile.formattedRanges] : [];
    // If a range has been found, identify the index of it in the list of ranges
    const index = range ? updatedRanges.indexOf(range) : -1;
    // If the delta happened within a formatted range, adjust the range length
    if (index !== -1) {
        // Operation extends beyond this formatted range
        if (textTileDelta.position + (textTileDelta.length || 0) > range.start + range.length) {
            range.length = textTileDelta.position + ((_a = textTileDelta.content) !== null && _a !== void 0 ? _a : '').length - range.start;
        }
        else {
            range.length += ((_b = textTileDelta.content) !== null && _b !== void 0 ? _b : '').length - (textTileDelta.length || 0);
        }
        updatedRanges[index] = range;
    }
    // Shift any updated ranges that came after the updated range
    for (const updatedRange of updatedRanges.slice(index + 1)) {
        if (updatedRange.start < textTileDelta.position) {
            continue;
        }
        // Operation extended into (or beyond) current range
        if (textTileDelta.position + (textTileDelta.length || 0) > updatedRange.start) {
            updatedRange.length = (updatedRange.start + updatedRange.length) - (textTileDelta.position + (textTileDelta.length || 0));
            updatedRange.start = textTileDelta.position + ((_c = textTileDelta.content) !== null && _c !== void 0 ? _c : '').length;
        }
        else {
            updatedRange.start += ((_d = textTileDelta.content) !== null && _d !== void 0 ? _d : '').length - (textTileDelta.length || 0);
        }
    }
    // Remove any ranges with zero-length (or less) after a delete or update operation
    updatedRanges = updatedRanges.filter((range) => range.length > 0);
    return updatedRanges;
}
/**
 * Get the updated attribution ranges of a formatted text tile based on the delta
 * @param textTile the text tile to apply the delta
 * @param textTileDelta the delta to apply
 * @returns An array of updated attribution ranges based on the formatted text tile after the delta is applied
 */
function getUpdatedAttributionRangeFromFormattedTextTileDelta(textTile, textTileDelta) {
    var _a, _b, _c, _d, _e;
    // This is needed for temporary backwards compatibility to support the old attributionRanges property, it can be removed once all consumers are updated to use new IFormattedTextTileDelta.attributionData
    const deltaAttributionRanges = ((_a = textTileDelta.attributionData) === null || _a === void 0 ? void 0 : _a.ranges) || textTileDelta.attributionRanges;
    if (((_b = textTileDelta.attributionData) === null || _b === void 0 ? void 0 : _b.isFullUpdate) || textTileDelta.deltaType !== TextTileDeltaType.AttributionUpdate && deltaAttributionRanges !== undefined) {
        // There are cases where the client can't send attribution ranges as a delta and will provide the full set of attribution ranges alongside the content update (add/update/delete)
        return deltaAttributionRanges;
    }
    if (!((_c = textTile.attributionRanges) === null || _c === void 0 ? void 0 : _c.length) && !deltaAttributionRanges
        || (textTileDelta.deltaType === TextTileDeltaType.Update && textTileDelta.length === 0)) {
        // Nothing to do, return the existing attribution ranges
        return textTile.attributionRanges;
    }
    // Start with the existing attribution ranges
    let updatedRanges = textTile.attributionRanges ? [...textTile.attributionRanges] : [];
    if (textTileDelta.deltaType !== TextTileDeltaType.AttributionUpdate) {
        // If the delta is not an attribution update, use position as that is the best we can do to ensure no mis-attributions
        const firstRangeForDelta = findRangeForDeltaType(textTile.attributionRanges, textTileDelta.deltaType, textTileDelta.position);
        // If a range has been found, identify the index of it in the list of ranges
        const index = firstRangeForDelta ? updatedRanges.indexOf(firstRangeForDelta) : -1;
        // If the delta happened within an attribution range, adjust the range length. Note that this is different from formatted ranges, as attribution ranges do not automatically extend, so the range should be cut off
        // and any unknown/new range should not have any attribution until the client sends an attribution update
        if (index !== -1) {
            // For now just cutoff the range at the delta position, in theory we could also split/adjust by creating another range after the delta, but the client should be sending everything after the delta as a delta update (better to have no attribution than incorrect attribution)
            firstRangeForDelta.length = textTileDelta.position - firstRangeForDelta.start;
        }
        // Shift any attribution ranges that came after the delta (ranges impacted by textTileDelta.position/textTileDelta.length/textTileDelta.content.length)
        const deltaContentLength = ((_d = textTileDelta.content) !== null && _d !== void 0 ? _d : '').length;
        const deltaLength = (_e = textTileDelta.length) !== null && _e !== void 0 ? _e : 0; // Delete/Update operation will impact the range start/length by deltaLength e.g. an update operation that adds 3 characters (deltaContentLength is 3) could also delete 4 characters in the same operation (deltaLength is 4)
        const deltaEndPosition = textTileDelta.position + deltaLength;
        const deltaContentEndPosition = textTileDelta.position + deltaContentLength;
        for (const currentRange of updatedRanges.slice(index + 1)) {
            if (currentRange.start < textTileDelta.position) {
                continue;
            }
            const currentRangeEnd = currentRange.start + currentRange.length;
            // Delta operation extended into (or beyond) current range
            if (deltaEndPosition > currentRange.start) {
                // If the range is completely covered by the delta (currentRangeEnd <= deltaEndPosition) length will now be <0 and will be filtered out after this loop
                // If not completely covered, length will be adjusted by the impact of the delta (delta length !== 0 i.e. delete or update and need to trim the length by the characters removed)
                currentRange.length = currentRangeEnd - deltaEndPosition;
            }
            // Either the range is after the delta and needs to be shifted by the delta content length/delta length or the delta overlaps into the range and the start needs to be adjusted to the end of the delta content
            currentRange.start = Math.max(currentRange.start + deltaContentLength - deltaLength /* range is shifted */, deltaContentEndPosition /*  range start is cutoff by overlapping delta */);
        }
        // Remove any ranges with zero-length (or less) after a delete or update operation
        updatedRanges = updatedRanges.filter((range) => range.length > 0);
        return updatedRanges;
    }
    // For efficiency create an array of {start,length} that combines the deltas without isolating attribution user. This is useful to remove old ranges that are no longer applicable regardless of user.
    // Since we iterate through the old deltas this helps us avoid iterating through the new deltas for each and instead we just need to check if the range is covered in the deltaCoverage array
    const deltaCoverage = [];
    for (const delta of deltaAttributionRanges) {
        if (deltaCoverage.length === 0) {
            deltaCoverage.push({ start: delta.start, length: delta.length });
            continue;
        }
        // If the previous delta is contiguous (or overlapping) with the current delta, extend the previous delta length. E.g. if userA has range 0,3 and userB has range 3,2 the previous range(0,3) should be extended to 0,5
        if (deltaCoverage[deltaCoverage.length - 1].start + deltaCoverage[deltaCoverage.length - 1].length >= delta.start) {
            deltaCoverage[deltaCoverage.length - 1].length = delta.start + delta.length - deltaCoverage[deltaCoverage.length - 1].start;
        }
        else {
            // Not overlapping or contiguous add a new entry with the start and length of the delta
            deltaCoverage.push({ start: delta.start, length: delta.length });
        }
    }
    // This function is needed for the case where the client sends full attribution ranges in the AttributionUpdate delta, should no-op for add/update/delete since they shouldn't have attribution ranges
    // if this ends up being costly it might make sense to send a flag with the delta to indicate it is a full set of attribution ranges because then we can straight up replace, but this could be helpful
    // since it would also help fix any issues with the client accidentally sending more than they should (but still accurate) in the delta
    const isCoveredByDelta = (range) => {
        for (const delta of deltaCoverage) {
            if (delta.start <= range.start && delta.start + delta.length >= range.start + range.length) {
                return true;
            }
        }
        return false;
    };
    // Remove any ranges with zero-length (or less) after a delete or update operation or any ranges that are obsolete due to the delta
    updatedRanges = updatedRanges.filter((range) => range.length > 0 && !isCoveredByDelta(range));
    // Now add the attribution ranges from the delta
    deltaAttributionRanges === null || deltaAttributionRanges === void 0 ? void 0 : deltaAttributionRanges.forEach((range) => {
        updatedRanges.push(range);
    });
    // Sort the merged attribution ranges and trim any overlapping ranges
    updatedRanges.sort((a, b) => a.start - b.start);
    const trimmedAttributionRanges = [];
    for (const range of updatedRanges) {
        const previousRange = trimmedAttributionRanges.length > 0 ? trimmedAttributionRanges[trimmedAttributionRanges.length - 1] : undefined;
        // Check if we need to adjust the length of the previous range e.g. if the client is splitting a range and only provides split+subsequent ranges, the pre split range should be adjusted
        if (previousRange && previousRange.start + previousRange.length > range.start) {
            previousRange.length = Math.max(range.start - previousRange.start, 0);
        }
        // Check if current range has equivalent attribution info and starts within or immediately after the previous range
        if (!previousRange || !areAttributionRangesMergeable(previousRange, range) || range.start > previousRange.start + previousRange.length) {
            // Either the first range or a unique attribution range
            trimmedAttributionRanges.push(range);
        }
        else {
            // Combine with the last range since the attributions are the same
            previousRange.length += Math.max(range.start + range.length - (previousRange.start + previousRange.length), 0);
        }
    }
    return trimmedAttributionRanges;
}
export function applyFormattedTextTileDelta(delta, tile, clientMetadataFlights) {
    var _a, _b, _c, _d;
    const applyDeltaOp = new LogOperation({
        operationName: 'ApplyFormattedTextTileDeltaForLocalWorkflows',
        dimension0: '0',
        success: true
    }).start();
    // This is temporary just to measure some ratio of differente client settings for this feature
    // Will be removed once we have enough data, already added to cleanup PR: https://dev.azure.com/office/ISS/_git/augloop-server/pullrequest/1723332
    applyDeltaOp.clientFlights = clientMetadataFlights;
    try {
        if (!tile) {
            applyDeltaOp.success = false;
            applyDeltaOp.resultDescription = 'Unable to apply formatted text tile delta, parent tile is undefined';
            Log.info(0x201d6861 /* tag_6hw77 */, LogCategory.CoreDefault, applyDeltaOp.stop());
            return undefined;
        }
        if (SchemaObject.getTypeNameFor(tile) !== FormattedTextTile.getTypeName()) {
            applyDeltaOp.success = false;
            applyDeltaOp.resultDescription = `Unable to apply formatted text tile delta, parent tile is not proper type: expected ${FormattedTextTile.getTypeName()}, received ${SchemaObject.getTypeNameFor(tile)}`;
            Log.info(0x201d6862 /* tag_6hw78 */, LogCategory.CoreDefault, applyDeltaOp.stop());
            return undefined;
        }
        const textTile = tile;
        const textTileDelta = delta;
        const content = textTile.content;
        let updatedContent = '';
        if ((textTileDelta.position === undefined || textTileDelta.position < 0) && !isNonContentOrDeleteDelta(textTileDelta.deltaType)) {
            applyDeltaOp.success = false;
            applyDeltaOp.resultDescription = 'Unable to apply formatted text tile delta, invalid text tile position';
            Log.info(0x201d6863 /* tag_6hw79 */, LogCategory.CoreDefault, applyDeltaOp.stop());
            return undefined;
        }
        else if (textTileDelta.content === undefined && !isNonContentOrDeleteDelta(textTileDelta.deltaType)) {
            applyDeltaOp.success = false;
            applyDeltaOp.resultDescription = 'Unable to apply formatted text tile delta, non-delete, non-formatting, non-other-non-content-update and non-cursor-update operation without content defined';
            Log.info(0x201d6880 /* tag_6hw8a */, LogCategory.CoreDefault, applyDeltaOp.stop());
            return undefined;
        }
        if (textTileDelta.deltaType === TextTileDeltaType.Add && textTileDelta.length !== 0) {
            Log.info(0x201d6881 /* tag_6hw8b */, LogCategory.CoreDefault, new LogOperation({ operationName: 'ApplyDeltaChecks', resultDescription: 'Received formatted text tile delta add operation with delta length, expected length 0', success: true }));
        }
        else if (isNonContentOrDeleteDelta(textTileDelta.deltaType) && textTileDelta.content !== undefined) {
            Log.info(0x201d6882 /* tag_6hw8c */, LogCategory.CoreDefault, new LogOperation({ operationName: 'ApplyDeltaChecks', resultDescription: 'Received formatted text tile delta delete or non-content related operation with content defined, expected undefined', success: true }));
        }
        if (!isNonContentDelta(textTileDelta.deltaType)) {
            const updatedFormattedRanges = getUpdatedFormattedRangeFromFormattedTextTileDelta(textTile, textTileDelta);
            const updatedAttributionRanges = getUpdatedAttributionRangeFromFormattedTextTileDelta(textTile, textTileDelta);
            let updatedIpPosition = textTile.ipPosition;
            // Apply content changes and new IP position
            switch (textTileDelta.deltaType) {
                case TextTileDeltaType.Add:
                    if (textTileDelta.position < content.length) {
                        updatedContent = `${content.slice(0, textTileDelta.position)}${textTileDelta.content}${content.slice(textTileDelta.position, content.length)}`;
                    }
                    else {
                        updatedContent = content + textTileDelta.content;
                    }
                    updatedIpPosition = textTileDelta.position + textTileDelta.length;
                    break;
                case TextTileDeltaType.Update:
                    if (!textTileDelta.content && textTileDelta.unit === TextTileDeltaUnit.Sentence) {
                        applyDeltaOp.dimension0 = '1';
                    }
                    updatedContent = `${content.slice(0, textTileDelta.position)}${textTileDelta.content}${content.slice(textTileDelta.position + ((_a = textTileDelta.length) !== null && _a !== void 0 ? _a : 0), content.length)}`;
                    updatedIpPosition = textTileDelta.position + textTileDelta.content.length;
                    break;
                case TextTileDeltaType.Delete:
                    updatedContent = `${content.slice(0, textTileDelta.position)}${content.slice(textTileDelta.position + ((_b = textTileDelta.length) !== null && _b !== void 0 ? _b : 0), content.length)}`;
                    updatedIpPosition = textTileDelta.position;
                    break;
            }
            // Consolidate to return new FormattedTextTile({...}) after logging removed.
            const returnTile = new FormattedTextTile(Object.assign(Object.assign({}, textTile), { ipPosition: updatedIpPosition, content: updatedContent, formattedRanges: updatedFormattedRanges, attributionRanges: updatedAttributionRanges, queryRange: textTileDelta.queryRange }));
            Log.info(0x201e0051 /* tag_6h6br */, LogCategory.CoreDefault, applyDeltaOp.stop());
            return returnTile;
        }
        else if (textTileDelta.deltaType === TextTileDeltaType.CursorUpdate) {
            return new FormattedTextTile(Object.assign(Object.assign({}, textTile), { ipPosition: (_c = textTileDelta.cursorData) === null || _c === void 0 ? void 0 : _c.ipPosition, isColdIp: (_d = textTileDelta.cursorData) === null || _d === void 0 ? void 0 : _d.isColdIp }));
        }
        else if (textTileDelta.deltaType === TextTileDeltaType.FormattingUpdate) {
            return new FormattedTextTile(Object.assign(Object.assign({}, textTile), { formattedRanges: textTileDelta.formattedRanges }));
        }
        else if (textTileDelta.deltaType === TextTileDeltaType.OtherNonContentUpdate) {
            let property;
            const diff = {};
            for (property in textTileDelta.otherNonContentData) {
                diff[property] = textTileDelta.otherNonContentData[property];
            }
            return new FormattedTextTile(Object.assign(Object.assign({}, textTile), diff));
        }
        else if (textTileDelta.deltaType === TextTileDeltaType.AttributionUpdate) {
            return new FormattedTextTile(Object.assign(Object.assign({}, textTile), { attributionRanges: getUpdatedAttributionRangeFromFormattedTextTileDelta(textTile, textTileDelta) }));
        }
        return undefined;
    }
    catch (error) {
        applyDeltaOp.success = false;
        applyDeltaOp.resultDescription = `Error applying formatted text tile delta: ${error}`;
        Log.info(0x201d6883 /* tag_6hw8d */, LogCategory.CoreDefault, applyDeltaOp.stop());
        return undefined;
    }
}
export function findRangeForDeltaType(ranges, deltaType, position) {
    const findRangeLogOp = new LogOperation({
        operationName: 'FindRangeForDelta',
        success: true
    });
    findRangeLogOp.start();
    // Check if there are any ranges to look through
    if (!ranges) {
        findRangeLogOp.resultDescription = 'No formatted ranges found within the tile, skipping.';
        Log.info(0x201d6884 /* tag_6hw8e */, LogCategory.CoreDefault, findRangeLogOp.stop());
        return undefined;
    }
    // Add operations generally look to the position one before them when determining what formatted
    // range to extend, the edge cases being the start of a tile (position 0) or the start of a zero-length formatted range.
    // Check for these edge cases
    if (deltaType === TextTileDeltaType.Add) {
        const zeroLengthRangeAtPosition = ranges.find((range) => range.start === position && range.length === 0);
        if (zeroLengthRangeAtPosition) {
            findRangeLogOp.resultDescription = `Found a zero-length formatted range for add operation with start ${zeroLengthRangeAtPosition.start}.`;
            Log.info(0x201d6885 /* tag_6hw8f */, LogCategory.CoreDefault, findRangeLogOp.stop());
            return zeroLengthRangeAtPosition;
        }
        position = Math.max(position - 1, 0);
    }
    // Check for the range wherein the position is between (inclusive) the start and end of the range
    const foundRange = ranges.find((range) => {
        if (range.length === 0) {
            return range.start === position;
        }
        return range.start <= position && position < (range.start + range.length);
    });
    if (foundRange) {
        findRangeLogOp.resultDescription = `Updating formatted range for operation with start: ${foundRange.start} and length: ${foundRange.length}`;
        Log.info(0x201d6886 /* tag_6hw8g */, LogCategory.CoreDefault, findRangeLogOp.stop());
    }
    else {
        findRangeLogOp.resultDescription = `Unable to find formatted range for operation at position ${position}, skipping.`;
        Log.info(0x201d6887 /* tag_6hw8h */, LogCategory.CoreDefault, findRangeLogOp.stop());
    }
    return foundRange;
}
function isNonContentDelta(deltaType) {
    return nonContentDeltaTypes.indexOf(deltaType) !== -1;
}
function isNonContentOrDeleteDelta(deltaType) {
    return isNonContentDelta(deltaType) || TextTileDeltaType.Delete === deltaType;
}
function areAttributionRangesMergeable(range1, range2) {
    return range1.attribution.userId === range2.attribution.userId && range1.attribution.timestamp === range2.attribution.timestamp && range1.attribution.dataSource === range2.attribution.dataSource;
}
//# sourceMappingURL=delta-handlers.js.map